/**
 * P3-08 · Cart mutation contract + pure stock math.
 *
 * The shared seam between the PDP buy-box actions and the cart backend (P4).
 * This module is **client-safe** (no server-only imports) so both the client
 * store (`useCart`) and the server-authoritative `cart.addItem` mutation import
 * the same input schema and the same purchasing bounds — the client can never
 * drift from what the server will enforce.
 *
 * Cart *persistence* (rows, totals, mini-cart) lands in P4; this ticket only
 * defines and consumes the mutation interface.
 */
import { z } from "zod";

/**
 * Per-product personalisation (monogram, engraving, dropdowns) — owned by
 * **P3-07**. Reserved here as an opaque, nullable slot so add-to-cart carries
 * it end-to-end the moment P3-07 lands, with no contract change. Until then it
 * is always `null`.
 */
export const personalisationSchema = z
  .record(z.string(), z.union([z.string(), z.boolean()]))
  .nullish()
  .transform((v) => v ?? null);

export type CartPersonalisation = z.infer<typeof personalisationSchema>;

/**
 * P6-14 · Gift-card purchase details for a gift-card product line. `amountMinor`
 * is the shopper's chosen custom amount (display-only — the server re-clamps to
 * the brand's min/max; a fixed-denomination variant ignores it and uses its own
 * price). Recipient/gifting fields ride the order to issuance.
 */
export const giftCardPurchaseSchema = z
  .object({
    recipientEmail: z.string().trim().email(),
    recipientName: z.string().trim().max(120).optional(),
    senderName: z.string().trim().max(120).optional(),
    message: z.string().trim().max(500).optional(),
    /** ISO date; the recipient email is scheduled for this day (else sent now). */
    deliveryAt: z.string().datetime().optional(),
    /** Custom face value in minor units (only for the custom-amount variant). */
    amountMinor: z.number().int().positive().optional(),
  })
  .nullish()
  .transform((v) => v ?? null);

export type GiftCardPurchaseInput = z.infer<typeof giftCardPurchaseSchema>;

/** Input for `cart.addItem` — the P4 interface, consumed now against a stub. */
export const addItemInputSchema = z.object({
  variantId: z.string().min(1),
  quantity: z.number().int().min(1).max(999),
  personalisation: personalisationSchema,
  /** P6-14 · Present only when adding a gift-card product. */
  giftCard: giftCardPurchaseSchema.optional(),
});

export type AddItemInput = z.infer<typeof addItemInputSchema>;

/** Result of a successful add — kept minimal until P4 returns cart state. */
export interface AddItemResult {
  ok: true;
  variantId: string;
  quantity: number;
}

/** The stock-relevant slice of a variant the bounds math needs. */
export interface PurchasableVariant {
  stock: number;
  isPreorder: boolean;
  backorderable: boolean;
  manageStock?: boolean | null;
  manualStockStatus?: string | null;
}

/**
 * Maximum units of a variant that can be added in one go.
 *
 * A pre-order or backorderable variant is purchasable beyond its physical
 * stock, so it clamps to the config `backorderCap` rather than `stock`; every
 * other variant is bounded by on-hand `stock` (0 → not purchasable). Pure and
 * shared, so the client stepper and the server guard agree exactly.
 */
export function maxPurchasable(
  variant: PurchasableVariant,
  backorderCap: number,
): number {
  if (variant.manageStock === false) {
    return variant.manualStockStatus === "OUT_OF_STOCK"
      ? 0
      : Math.max(backorderCap, 0);
  }
  if (variant.isPreorder || variant.backorderable) {
    return Math.max(backorderCap, 0);
  }
  return Math.max(variant.stock, 0);
}

/** Whether any quantity of this variant can be added right now. */
export function isPurchasable(
  variant: PurchasableVariant,
  backorderCap: number,
): boolean {
  return maxPurchasable(variant, backorderCap) >= 1;
}

/**
 * Clamp a desired quantity into `[1, max]`. Non-finite input (a cleared typed
 * field) falls back to the minimum; a `max` of 0 (out of stock) returns 0 so
 * the caller can treat it as not-addable. Fractions floor.
 */
export function clampQuantity(desired: number, max: number): number {
  if (max <= 0) return 0;
  if (!Number.isFinite(desired)) return 1;
  const floored = Math.floor(desired);
  if (floored < 1) return 1;
  if (floored > max) return max;
  return floored;
}
